import React from "react";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import { DialogShell } from "@/shared/ui/DialogShell";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Free-text details the officer wants the agent to know while writing. */
  additionalContext: string;
  onAdditionalContextChange: (value: string) => void;
  /** Starts the job. The field is optional, so this is always available. */
  onGenerate: () => void;
  isGenerating: boolean;
}

/**
 * Last stop before the job starts: asks for anything the footage doesn't show.
 * Deliberately skippable — the context is optional, and an officer with nothing
 * to add should be one click from generating.
 */
export const AdditionalContextDialog: React.FC<Props> = ({
  open,
  onClose,
  additionalContext,
  onAdditionalContextChange,
  onGenerate,
  isGenerating,
}) => (
  <DialogShell
    open={open}
    onClose={onClose}
    // A job in flight is not cancellable from here, so the dialog stays put
    // until the page navigates away from it.
    closeDisabled={isGenerating}
    size="sm"
    titleId="additional-context-dialog-title"
  >
    <DialogTitle id="additional-context-dialog-title" sx={{ pr: 6 }}>
      Add details about this incident?
    </DialogTitle>
    <DialogContent>
      <DialogContentText sx={{ mb: 2 }}>
        Anything you know that the footage doesn't show — case numbers, names,
        what happened off-camera — helps the agent write an accurate report.
        This is optional; you can generate without it.
      </DialogContentText>
      <TextField
        label="Additional context (optional)"
        placeholder="Details the agent should know while writing this report"
        value={additionalContext}
        onChange={(event) => onAdditionalContextChange(event.target.value)}
        disabled={isGenerating}
        multiline
        minRows={4}
        maxRows={10}
        fullWidth
        autoFocus
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={isGenerating} color="inherit">
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={onGenerate}
        disabled={isGenerating}
        startIcon={
          isGenerating ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <BoltIcon />
          )
        }
      >
        {isGenerating ? "Generating..." : "Generate report"}
      </Button>
    </DialogActions>
  </DialogShell>
);
